import { useState, useEffect, useCallback } from 'react';
import { api } from '../api/client';
import { useShop } from '../hooks/useShop';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ErrorState from '../components/shared/ErrorState';
import { formatMoney, formatDate } from '../utils/formatters';

export default function StatementUploadPage() {
  const { shops } = useShop();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);
  const [stmtDetail, setStmtDetail] = useState(null);

  // Upload form state
  const [vendorId, setVendorId] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [statementDate, setStatementDate] = useState('');
  const [file, setFile] = useState(null);

  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getStatements();
      setStatements(data.statements || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file || !vendorName || !statementDate) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('vendor_id', vendorId || '0');
      formData.append('vendor_name', vendorName);
      formData.append('statement_date', statementDate);
      formData.append('user', 'Dashboard User');

      await api.uploadFile('/api/statements/upload', formData);
      setShowUpload(false);
      setFile(null);
      setVendorName('');
      setStatementDate('');
      fetchStatements();
    } catch (err) {
      alert('Upload failed: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const viewStatement = async (id) => {
    setSelectedStatement(id);
    try {
      const detail = await api.getStatement(id);
      setStmtDetail(detail);
    } catch (err) {
      alert('Failed to load statement: ' + err.message);
    }
  };

  if (loading) return <LoadingSpinner text="Loading statements..." />;
  if (error) return <ErrorState message={error} onRetry={fetchStatements} />;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-800 mb-1">Statement Reconciliation</h1>
          <p className="text-sm text-gray-400">Upload vendor statements to match credits against your return bucket</p>
        </div>
        <button
          onClick={() => setShowUpload(!showUpload)}
          className="px-5 py-2.5 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-700"
        >
          + Upload Statement
        </button>
      </div>

      {showUpload && (
        <form onSubmit={handleUpload} className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor Name</label>
              <input
                type="text"
                value={vendorName}
                onChange={(e) => setVendorName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                placeholder="e.g. Transtar"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Statement Date</label>
              <input
                type="date"
                value={statementDate}
                onChange={(e) => setStatementDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Vendor ID (optional)</label>
              <input
                type="number"
                value={vendorId}
                onChange={(e) => setVendorId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                placeholder="Tekmetric vendor ID"
              />
            </div>
          </div>

          <div
            className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center mb-4 bg-gray-50"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              setFile(e.dataTransfer.files[0]);
            }}
          >
            {file ? (
              <div>
                <div className="text-2xl mb-2">&#128196;</div>
                <div className="text-sm font-semibold text-gray-700">{file.name}</div>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="text-xs text-red-500 mt-2 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div>
                <div className="text-2xl mb-2">&#128196;</div>
                <div className="text-sm font-semibold text-gray-600 mb-1">Drop vendor statement here</div>
                <div className="text-xs text-gray-400 mb-3">Supports PDF, CSV, or Excel</div>
                <label className="px-4 py-2 bg-gray-200 rounded-lg text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-300">
                  Browse Files
                  <input type="file" className="hidden" onChange={(e) => setFile(e.target.files[0])} accept=".pdf,.csv,.xlsx,.xls" />
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setShowUpload(false)}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={uploading || !file}
              className="px-5 py-2 bg-gray-800 text-white rounded-lg text-sm font-semibold hover:bg-gray-700 disabled:opacity-50"
            >
              {uploading ? 'Uploading...' : 'Upload & Parse'}
            </button>
          </div>
        </form>
      )}

      {/* Statement list */}
      <div className="space-y-4">
        {statements.length === 0 && (
          <div className="text-center py-16 text-gray-400">
            <div className="text-3xl mb-3">&#128196;</div>
            <div className="font-semibold">No statements uploaded yet</div>
            <div className="text-xs mt-1">Upload a vendor statement to start reconciling credits</div>
          </div>
        )}

        {statements.map((stmt) => (
          <div key={stmt.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="flex justify-between items-center p-5 border-b border-gray-100">
              <div>
                <div className="font-bold text-base text-gray-800">{stmt.vendor_name}</div>
                <div className="text-xs text-gray-400">
                  Statement: {formatDate(stmt.statement_date)} &middot; Uploaded {formatDate(stmt.upload_date)}
                  &middot; {stmt.filename}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span
                  className={`px-3 py-1 rounded-full text-[11px] font-semibold ${
                    stmt.parse_status === 'COMPLETED'
                      ? 'bg-green-50 text-green-700'
                      : stmt.parse_status === 'PARSING'
                        ? 'bg-blue-50 text-blue-700'
                        : stmt.parse_status === 'FAILED'
                          ? 'bg-red-50 text-red-700'
                          : 'bg-amber-50 text-amber-700'
                  }`}
                >
                  {stmt.parse_status}
                </span>
                <button
                  onClick={() => viewStatement(stmt.id)}
                  className="text-xs text-blue-600 font-semibold hover:underline"
                >
                  View Details
                </button>
              </div>
            </div>

            {stmt.total_credits_usd != null && (
              <div className="flex gap-8 px-5 py-3 text-sm">
                <div>
                  <span className="text-gray-400 text-xs">Credits:</span>{' '}
                  <span className="font-bold text-green-700">{formatMoney(stmt.total_credits_usd)}</span>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Charges:</span>{' '}
                  <span className="font-bold">{formatMoney(stmt.total_charges_usd)}</span>
                </div>
                <div>
                  <span className="text-gray-400 text-xs">Net:</span>{' '}
                  <span className={`font-extrabold ${(stmt.net_balance_usd || 0) < 0 ? 'text-green-700' : 'text-red-700'}`}>
                    {formatMoney(Math.abs(stmt.net_balance_usd || 0))} {(stmt.net_balance_usd || 0) < 0 ? 'credit' : 'owed'}
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Statement detail modal */}
      {selectedStatement && stmtDetail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[80vh] overflow-y-auto p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-bold">{stmtDetail.vendor_name} Statement</h3>
                <p className="text-xs text-gray-400">
                  {formatDate(stmtDetail.statement_date)} &middot; Status: {stmtDetail.parse_status}
                </p>
              </div>
              <button onClick={() => { setSelectedStatement(null); setStmtDetail(null); }} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>

            {stmtDetail.line_items?.length > 0 ? (
              <div className="space-y-2">
                {stmtDetail.line_items.map((item) => (
                  <div
                    key={item.id}
                    className={`flex justify-between items-center p-3 rounded-lg border ${
                      item.matched
                        ? 'bg-green-50 border-green-200'
                        : item.type === 'credit'
                          ? 'bg-amber-50 border-amber-200'
                          : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">{item.matched ? '\u2713' : '?'}</span>
                      <div>
                        <div className="text-sm font-semibold">{item.description}</div>
                        {item.invoice_number && <div className="text-xs text-gray-400">Invoice: {item.invoice_number}</div>}
                        {item.matched && (
                          <div className="text-[11px] text-green-600">
                            Matched to {item.matched_po_id ? `PO #${item.matched_po_id}` : `Return #${item.matched_return_order_id}`}
                            {item.match_confidence && ` (${Math.round(item.match_confidence * 100)}%)`}
                          </div>
                        )}
                      </div>
                    </div>
                    <span className={`font-bold text-sm ${item.type === 'credit' ? 'text-green-700' : ''}`}>
                      {formatMoney(item.amount_usd)}
                    </span>
                  </div>
                ))}

                <div className="flex justify-between pt-3 border-t mt-3">
                  <span className="text-sm text-gray-500">
                    {stmtDetail.matched_count} matched &middot; {stmtDetail.unmatched_count} unmatched
                  </span>
                  {stmtDetail.net_balance_usd != null && (
                    <span className="font-extrabold">
                      Net: {formatMoney(Math.abs(stmtDetail.net_balance_usd))}
                    </span>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-400">
                <div className="text-2xl mb-2">&#128196;</div>
                <div className="text-sm">
                  {stmtDetail.parse_status === 'PENDING'
                    ? 'Statement parsing has not started yet'
                    : stmtDetail.parse_status === 'PARSING'
                      ? 'Statement is being parsed...'
                      : 'No line items found'}
                </div>
                {stmtDetail.parse_error && (
                  <div className="text-xs text-red-500 mt-2">{stmtDetail.parse_error}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
